import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMe, syncMeToLocalStorage } from '../services/auth/authService';
import { queryClient } from '../components/queryClient';
import { getTenantSettings, TENANT_SETTINGS_QUERY_KEY } from '../services/tenantSettings/tenantSettingsService';
import { defaultSiteIdQueryOptions } from '../services/iniTable/iniTableService';
import InactivityLogoutGuard from './InactivityLogoutGuard';

export default function RequireAuth({ children }) {
    const location = useLocation();

    const meQ = useQuery({
        queryKey: ['me'],
        queryFn: ({ signal }) => getMe(signal),
        retry: false,
        staleTime: 60_000,
    });

    // React Query v5 removed onSuccess — sync /me into localStorage after refresh/login.
    useEffect(() => {
        if (!meQ.data) return;
        syncMeToLocalStorage(meQ.data);
    }, [meQ.data]);

    // When already authenticated (e.g. page refresh), prefetch tenant settings + default SiteId
    useEffect(() => {
        if (!meQ.isSuccess) return;
        queryClient.prefetchQuery({
            queryKey: TENANT_SETTINGS_QUERY_KEY,
            queryFn: ({ signal }) => getTenantSettings(signal),
            staleTime: Infinity,
            gcTime: Infinity,
        });
        queryClient.prefetchQuery(defaultSiteIdQueryOptions);
    }, [meQ.isSuccess]);

    if (meQ.isLoading) {
        return null;
    }

    return meQ.isSuccess ? (
        <InactivityLogoutGuard>{children}</InactivityLogoutGuard>
    ) : (
        <Navigate to="/login" state={{ from: location }} replace />
    );
}
